import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Split, SplitItem, Truncate, GalleryItem } from '@patternfly/react-core';
import _ from 'lodash';
import queryString from 'query-string';

import { useTranslation } from 'localization/useTranslation';
import DescriptionCard from 'components/DescriptionCard/DescriptionCard';
import useAPI from 'services/useAPI';
import useApiCall from 'services/useApiCall';
import { TIME_VIEW } from 'components/Time/TimeView';
import useBoolean from 'utils/hooks/useBoolean';
import ViewModal from 'components/Modals/ViewModal';
import SimpleTable from 'components/Common/SimpleTable/SimpleTable';
import { useHistory, useLocation } from 'react-router-dom';

const ProvisioningCard = ({ provisionData, isLoading = false, updateProvisionData }) => {
  const api = useAPI();
  const T = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const [providerClient, setProviderClient] = useState();
  const [template, setTemplate] = useState();
  const [templateError, setTemplateError, clearTemplateError] = useBoolean(false);
  const [viewComponent, setViewComponent] = useState(null);


  const showTemplate = useCallback(() => {
    if (template?.id) {
      return (
        <Button onClick={() => history.push({
          pathname: `${location.pathname}/templates/${template?.id}`,
          search: queryString.stringify({ readonly: true })
        })}
        variant="secondary"
        isDisabled={templateError}
        size="sm"
        isBlock>{ (!_.isEmpty(template?.displayName)) ? template?.displayName : <Truncate content={template?.id} /> }</Button>
      );
    }
    return <></>;
  }, [history, location.pathname, template, templateError]);


  const showSignalsModal = useCallback(() => {
    const model = [
      { label: T.common.id, path: 'id' }
    ];
    const comp = { title: T.common.signals, component: <SimpleTable compact columns={model} data={provisionData?.options?.signals}/> };
    if (provisionData?.options?.signals) {
      return (
        <Button variant="secondary" size="sm" isBlock onClick={() => setViewComponent(comp)}>{ T.provisioningCard.showSignals }</Button>
      );
    }
    return <></>;
  }, [T.common.id, T.common.signals, T.provisioningCard.showSignals, provisionData?.options?.signals]);

  const providerClientModels = useMemo(() => {
    function signals() {
      if (provisionData?.options?.signals) {
        return { label: T.common.signals, path: '', CustomComponent: showSignalsModal };
      }
      return {};
    }
    return [
      { label: T.common.template, path: 'templateId', CustomComponent: showTemplate },
      { label: T.common.timestamp, path: 'timestamp', time: TIME_VIEW.short },
      signals()
    ];
  }, [T.common.signals, T.common.template, T.common.timestamp, provisionData?.options?.signals, showSignalsModal, showTemplate]);


  useEffect(function updateInternalInfo() {
    setProviderClient(provisionData);
  }, [provisionData]);


  const [updateProviderClient, isUpdating] = useApiCall({
    apiCall: api.core.updateProviderClient,
    onSuccess: useCallback(() => {
      // setAsset({
      //   ...assetInfo.asset,
      //   modelDisplayName: assetInfo?.model?.displayName
      // });
    }, []),
    onError: useCallback((error) => {
      console.error(error);
    }, [])
  });

  const [getProviderClientTemplate] = useApiCall({
    apiCall: api.core.getProviderClientTemplate,
    onSuccess: useCallback((template) => {
      setTemplate(template);
      clearTemplateError();
    }, [clearTemplateError]),
    onError: useCallback((error) => {
      setTemplate({ id: 'no template found', displayName: 'no template found' });
      setTemplateError();
      console.error(error);
    }, [setTemplateError])
  });

  useEffect(function fetchTemplate() {
    if (provisionData?.templateId) {
      getProviderClientTemplate(provisionData?.templateId);
    }
  }, [getProviderClientTemplate, provisionData?.templateId]);


  const cardFooter = useMemo(() => {
    const a1 = _.pick(providerClient, ['displayName', 'description']);
    const a2 = _.pick(provisionData, ['displayName', 'description']);
    if (!_.isEqual(a1, a2)) {
      return (
        <Split hasGutter>
          <SplitItem><Button
            isLoading={isUpdating}
            onClick={() => {
              updateProviderClient(
                provisionData?.provider.id,
                provisionData.id,
                _.pick(provisionData, ['displayName', 'description']));
            }}>{ T.common.update }</Button>
          </SplitItem>
          <SplitItem><Button onClick={() => {
            updateProvisionData(provisionData.provider.id, provisionData.id);
          }}>{ T.common.cancel }</Button>
          </SplitItem>
        </Split>
      );
    }
    return null;
  }, [T.common.cancel, T.common.update, isUpdating, providerClient, provisionData, updateProviderClient, updateProvisionData]);


  return (
    (providerClient) ?
      <GalleryItem>
        <DescriptionCard
          models={providerClientModels}
          item={providerClient} setItem={setProviderClient}
          heading={T.common.provisionData}
          isLoading={isLoading}
          footer={cardFooter}
        />
        <ViewModal
          title={viewComponent?.title}
          Component={viewComponent?.component}
          onClose={() => setViewComponent(null)}
        />
      </GalleryItem>
      : <></>
  );
};

export default ProvisioningCard;
